import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight, Calendar, CheckCircle, ChevronDown, ChevronLeft, ChevronRight,
  ClipboardList, History, Info, MapPin, Pencil, Plus, Settings, Shield, Trash2, AlertCircle, Truck,
} from 'lucide-react';
import './PayrollTimesheetEditPage.css';
import config from '../../configLoader';
import { useSiteContext } from '../../SiteContext';
import { Page } from '../../interfaces/PagesInterface';
import TimesheetEditManager from '../../managers/TimesheetEditManager';
import {
  PayrollEntryResult,
  PayrollTimesheetState,
  PayrollTimesheetStateEditable,
  TimesheetPreviewEditable,
} from '../../interfaces/TimesheetEditInterface';
import {
  Day, EmployeeCustomerServiceWarranty, EmployeeOperationalTask, EmployeeSickClaim, EmployeeWorkOrderTask,
  EmployeeTask, HourlyProject, MiscEmployeeTask, TimeEntry, TimeEntryHourly,
} from '../../interfaces/EmployeeInterface';
import { TimeEntryType } from '../../interfaces/TimesheetInterface';
import { formatHours } from '../../util/hoursFormat';
import ConfirmModal from '../../components/ConfirmModal';
import InactiveBadge from '../../components/payrollEdit/InactiveBadge';
import PayrollSubmitWizard from '../../components/payrollEdit/PayrollSubmitWizard';
import HistoryPanel from '../../components/payrollEdit/HistoryPanel';
import HourlyEntryModal, { HourlyCarry, HourlyEntryTarget } from '../../components/payrollEdit/HourlyEntryModal';
import PieceEntryModal, { PieceCarry } from '../../components/payrollEdit/PieceEntryModal';
import PieceHoursModal, { PieceHoursCarry } from '../../components/payrollEdit/PieceHoursModal';
import {
  HourlyModeConfig, HourlyRowShape, classifyHourlyRow, resolveHourlyModeConfig,
} from '../../components/payrollEdit/hourlyModes';
import TaskClaimModal, { TaskCarry, TaskClaimTarget } from '../../components/payrollEdit/TaskClaimModal';
import MiscTaskModal, { MiscCarry, MiscTaskTarget } from '../../components/payrollEdit/MiscTaskModal';
import CswTaskModal, { CswCarry } from '../../components/payrollEdit/CswTaskModal';
import SickClaimModal, { SickCarry } from '../../components/payrollEdit/SickClaimModal';
import OperationalTaskModal, { OperationalCarry } from '../../components/payrollEdit/OperationalTaskModal';
import {
  ENTRY_KINDS, EditorKind, enabledEntryKinds,
} from '../../components/payrollEdit/entryKinds';
import {
  formatClockMinutes, instantToLocalWallTime, money, ymdParts,
} from '../../components/payrollEdit/clockParse';

/**
 * Payroll Timesheet Edit page (M2-B — TAS-64/65/66). The day-by-day editor
 * from the accepted design v2 bundle: identity header, informational session
 * bar (M3 slot), day cards with per-kind sections, modals for every kind with
 * the 10-key parser inside, PAYROLL/system provenance marks, the
 * already-submitted directed handoff, and the sticky totals footer fed by
 * GET /preview (server numbers only — no optimistic OT math anywhere).
 *
 * Preview cadence (closed decision 2): per-save, COALESCED — an in-flight
 * preview is dropped and refetched once when another save lands mid-flight; no
 * fixed debounce. One editSessionID GUID per page load rides on every CRUD call.
 */

// ── Helpers ──────────────────────────────────────────────────────────────────

function genEditSessionID(): string {
  const c: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && 'randomUUID' in c) return (c as { randomUUID(): string }).randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function formatPayPeriodRange(pp: { StartDate: string | Date; EndDate: string | Date }): string {
  const s = new Date(pp.StartDate);
  const e = new Date(pp.EndDate);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  const start = s.toLocaleDateString('en-US', opts);
  const end = s.getUTCMonth() === e.getUTCMonth()
    ? String(e.getUTCDate())
    : e.toLocaleDateString('en-US', opts);
  return `${start} – ${end}, ${e.getUTCFullYear()}`;
}

function initials(first?: string, last?: string): string {
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
}

/** Punch minutes from ISO instants (open punch contributes 0). */
function punchMinutes(inISO?: string | null, outISO?: string | null): number {
  if (!inISO || !outISO) return 0;
  const m = Math.round((Date.parse(outISO) - Date.parse(inISO)) / 60000);
  return Number.isFinite(m) && m > 0 ? m : 0;
}

// One editor row, normalized across the kinds. `shape` is the 2020 TEH shape
// ('legacy' on every tenant without clockHourlyV2Config).
type EditorEntry =
  | { kind: 'hourly' | 'pieceHours'; id: number; hourly: HourlyEntryTarget; shape: HourlyRowShape }
  | { kind: 'piece'; id: number; piece: TimeEntry }
  | { kind: 'task'; id: number; task: TaskClaimTarget }
  | { kind: 'miscTask'; id: number; misc: MiscTaskTarget }
  | { kind: 'cswTask'; id: number; csw: EmployeeCustomerServiceWarranty }
  | { kind: 'sickClaim'; id: number; sick: EmployeeSickClaim }
  | { kind: 'operationalTask'; id: number; op: EmployeeOperationalTask };

/** 2020 drive-time TEH rows — system-generated, shown read-only (never edited here). */
type DriveTimeRow = HourlyEntryTarget;

function collectDayEntries(
  day: Day,
  hourlyCfg: HourlyModeConfig
): { byKind: Record<EditorKind, EditorEntry[]>; driveTime: DriveTimeRow[] } {
  const out: Record<EditorKind, EditorEntry[]> = {
    pieceHours: [], hourly: [], piece: [], task: [], miscTask: [], cswTask: [], sickClaim: [],
    operationalTask: [],
  };
  const driveTime: DriveTimeRow[] = [];
  (day.hourlyProjects ?? []).forEach((group: HourlyProject) => {
    (group.timeEntriesHourly ?? []).forEach((row: TimeEntryHourly) => {
      // Without clockHourlyV2Config every row is 'legacy' -> the Hourly section, as always.
      const shape = classifyHourlyRow(row, group, hourlyCfg);
      if (shape === 'driveTime') {
        driveTime.push({ row, group });
      } else if (shape === 'pieceHours') {
        out.pieceHours.push({ kind: 'pieceHours', id: row.timeEntryHourlyID, hourly: { row, group }, shape });
      } else {
        out.hourly.push({ kind: 'hourly', id: row.timeEntryHourlyID, hourly: { row, group }, shape });
      }
    });
  });
  (day.timeEntries ?? []).forEach((row: TimeEntry) => {
    out.piece.push({ kind: 'piece', id: row.timeEntryID, piece: row });
  });
  (day.projectEmployeeTasks ?? []).forEach((pet) => {
    (pet.employeeTasks ?? []).forEach((row: EmployeeTask) => {
      out.task.push({ kind: 'task', id: row.employeeTaskID, task: { row, project: pet.project ?? null } });
    });
    (pet.miscEmployeeTasks ?? []).forEach((row: MiscEmployeeTask) => {
      out.miscTask.push({ kind: 'miscTask', id: row.miscEmployeeTaskID, misc: { row, project: pet.project ?? null } });
    });
  });
  (day.employeeCustomerServiceWarrantyTasks ?? []).forEach((row: EmployeeCustomerServiceWarranty) => {
    out.cswTask.push({ kind: 'cswTask', id: row.employeeCustomerServiceWarrantyID, csw: row });
  });
  (day.employeeSickClaims ?? []).forEach((row: EmployeeSickClaim) => {
    out.sickClaim.push({ kind: 'sickClaim', id: row.employeeSickClaimID, sick: row });
  });
  (day.employeeOperationalTasks ?? []).forEach((row: EmployeeOperationalTask) => {
    out.operationalTask.push({ kind: 'operationalTask', id: row.employeeOperationalTaskID, op: row });
  });
  return { byKind: out, driveTime };
}

/** The 2020 SO display — `SO 4412 · 24-118 Sunrise Ridge` (2020 SOs carry no name). */
function soRowLabel(row: TimeEntryHourly, group: HourlyProject): string {
  const so = row.serviceOrder?.altServiceOrderID ?? row.serviceOrderID ?? '';
  const job = [group.project?.altProjectID, group.project?.name].filter(Boolean).join(' ');
  return `SO ${so} · ${job}`.trim();
}

/** Delete-confirm line naming the entry (closed decision 3). */
function describeEntry(entry: EditorEntry): string {
  const clockSpan = (inISO?: string | null, inOff?: string | null, outISO?: string | null, outOff?: string | null) => {
    const i = instantToLocalWallTime(inISO ?? null, inOff ?? null);
    const o = outISO ? instantToLocalWallTime(outISO, outOff ?? null) : null;
    return `${i ? formatClockMinutes(i.minutes) : '—'} → ${o ? formatClockMinutes(o.minutes) : 'open'}`;
  };
  switch (entry.kind) {
    case 'pieceHours': {
      const g = entry.hourly.group;
      return `Piece hours punch ${clockSpan(entry.hourly.row.inDateTime, entry.hourly.row.inDateTimeOffset, entry.hourly.row.outDateTime, entry.hourly.row.outDateTimeOffset)} · ${g.project?.altProjectID ?? ''} · ${g.project?.name ?? ''}`;
    }
    case 'hourly': {
      const g = entry.hourly.group;
      const job = entry.shape === 'nonJob'
        ? 'Non-Job Time'
        : entry.shape === 'serviceOrder'
          ? soRowLabel(entry.hourly.row, g)
          : g.project?.projectID == null ? 'No job — bare hours' : `${g.project.altProjectID} · ${g.project.name}`;
      return `Hourly punch ${clockSpan(entry.hourly.row.inDateTime, entry.hourly.row.inDateTimeOffset, entry.hourly.row.outDateTime, entry.hourly.row.outDateTimeOffset)} · ${job}`;
    }
    case 'piece':
      return `${entry.piece.type === TimeEntryType.Meal ? 'Meal break' : 'Piece punch'} ${clockSpan(entry.piece.inDateTime, entry.piece.inDateTimeOffset, entry.piece.outDateTime, entry.piece.outDateTimeOffset)}`;
    case 'task':
      return `Task claim ${entry.task.row.task?.altTaskID ?? ''} · ${money(entry.task.row.amountClaimed ?? 0)}`;
    case 'miscTask':
      return `Misc task "${entry.misc.row.miscDescription}" · ${money(entry.misc.row.amountClaimed ?? 0)}`;
    case 'cswTask':
      return `CSW claim ${entry.csw.customerServiceWarranty?.name ?? ''} · ${formatHours(entry.csw.minutesClaimed ?? 0)}`;
    case 'sickClaim':
      return `Sick claim · ${formatHours(entry.sick.minutesClaimed ?? 0)}`;
    case 'operationalTask':
      return `Operational task ${entry.op.operationalTask?.name ?? ''} · ${formatHours(entry.op.minutesClaimed ?? 0)}`;
    default:
      return 'entry';
  }
}

interface ModalState {
  kind: EditorKind;
  ymd: string;
  entry: EditorEntry | null;
  carry: unknown;
  nonce: number;
}

// ── Component ────────────────────────────────────────────────────────────────

const PayrollTimesheetEditPage: React.FC = () => {
  const {
    setPage, payrollEditIntent, setReviewIntent, payPeriodID, payPeriods,
    createPopup, userName,
  } = useSiteContext();

  const employeeID = payrollEditIntent?.employeeID ?? null;
  const currentPayPeriod = payPeriods.find((p) => p.PayPeriodID === payPeriodID) ?? null;
  const periodLabel = currentPayPeriod ? formatPayPeriodRange(currentPayPeriod) : `Pay period ${payPeriodID}`;

  // One GUID per page load (§6.1) — every CRUD call of this sitting carries it.
  const editSessionID = useMemo(genEditSessionID, []);
  const sessionStart = useMemo(
    () => new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
    []
  );

  const [state, setState] = useState<PayrollTimesheetState | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [addMenuYmd, setAddMenuYmd] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ entry: EditorEntry; ymd: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [sessionChanges, setSessionChanges] = useState(0);
  const [flash, setFlash] = useState<string | null>(null);
  const flashTimer = useRef<number | null>(null);

  // Server preview (footer + per-day OT/DT chips).
  const [preview, setPreview] = useState<TimesheetPreviewEditable | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const previewRunning = useRef(false);
  const previewQueued = useRef(false);
  const previewSeq = useRef(0);

  const enabledKinds = useMemo(() => enabledEntryKinds(), []);
  const hourlyCfg = useMemo(() => resolveHourlyModeConfig(), []);

  // Close the day-header Add menu on any outside click.
  useEffect(() => {
    if (addMenuYmd == null) return undefined;
    const onDoc = () => setAddMenuYmd(null);
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [addMenuYmd]);

  const showFlash = useCallback((msg: string) => {
    setFlash(msg);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlash(null), 1400);
  }, []);

  // ── State load / reload ─────────────────────────────────────────────────────
  const reloadState = useCallback(async (initial = false) => {
    if (employeeID == null || !payPeriodID) return;
    if (initial) { setLoading(true); setLoadError(null); }
    try {
      const s = await TimesheetEditManager.loadState(employeeID, payPeriodID);
      setState(s);
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { message?: string } } };
      if (initial) {
        setLoadError(
          e?.response?.status === 404
            ? 'Employee not found.'
            : e?.response?.status === 403
              ? (e?.response?.data?.message ?? 'You do not have permission to edit timesheet entries.')
              : (e?.response?.data?.message ?? 'Could not load the payroll editing state.')
        );
      }
    } finally {
      if (initial) setLoading(false);
    }
  }, [employeeID, payPeriodID]);

  // ── Preview: per-save, coalesced (closed decision 2) ────────────────────────
  const runPreview = useCallback(async () => {
    if (employeeID == null || !payPeriodID) return;
    previewRunning.current = true;
    const seq = ++previewSeq.current;
    setPreviewBusy(true);
    try {
      const p = await TimesheetEditManager.loadPreview(employeeID, payPeriodID);
      if (seq === previewSeq.current) {
        if (p.exists) {
          // The period was submitted under us — the state reload shows the
          // directed handoff; the stale preview is dropped.
          reloadState();
        } else {
          setPreview(p);
        }
      }
    } catch {
      // Footer keeps its previous numbers; the next save refetches.
    } finally {
      previewRunning.current = false;
      if (previewQueued.current) {
        previewQueued.current = false;
        runPreview(); // exactly ONE refetch for however many saves landed mid-flight
      } else {
        setPreviewBusy(false);
      }
    }
  }, [employeeID, payPeriodID, reloadState]);

  const requestPreview = useCallback(() => {
    if (previewRunning.current) {
      // Drop the in-flight response (bump seq) and queue a single refetch.
      previewSeq.current += 1;
      previewQueued.current = true;
      setPreviewBusy(true);
      return;
    }
    runPreview();
  }, [runPreview]);

  useEffect(() => {
    reloadState(true);
    requestPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeID, payPeriodID]);

  // ── Save / delete pipeline ──────────────────────────────────────────────────
  const afterWrite = useCallback((result: PayrollEntryResult | null, verb: string, fieldCount: number) => {
    setSessionChanges((n) => n + Math.max(1, fieldCount));
    showFlash(`${verb} · logged`);
    reloadState();
    requestPreview();
  }, [reloadState, requestPreview, showFlash]);

  const handleModalSaved = useCallback((result: PayrollEntryResult, close: boolean, carry: unknown) => {
    const fields = result.changedFields?.length ?? 1;
    afterWrite(result, result.changedFields ? 'Saved' : 'Added', fields);
    setModal((m) => {
      if (!m) return null;
      if (close) return null;
      // Enter loop: fresh modal, same kind + day, carry prefills, in-field focused.
      return { kind: m.kind, ymd: m.ymd, entry: null, carry, nonce: m.nonce + 1 };
    });
  }, [afterWrite]);

  const handleConflict = useCallback((message: string) => {
    setModal(null);
    setDeleteTarget(null);
    setSubmitOpen(false);
    createPopup('Timesheet already submitted', message);
    reloadState();
  }, [createPopup, reloadState]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await TimesheetEditManager.deleteEntry(ENTRY_KINDS[deleteTarget.entry.kind].apiKind, deleteTarget.entry.id, editSessionID);
      setDeleteTarget(null);
      afterWrite(null, 'Deleted', 1);
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { message?: string } } };
      setDeleteTarget(null);
      if (e?.response?.status === 409) {
        handleConflict(e.response?.data?.message ?? 'Timesheet already submitted — unlock it first.');
      } else {
        createPopup('Delete failed', e?.response?.data?.message ?? 'Could not delete the entry. Please try again.');
      }
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, editSessionID, afterWrite, handleConflict, createPopup]);

  // ── Navigation ──────────────────────────────────────────────────────────────
  const goBoard = useCallback(() => {
    setPage(Page.TimesheetReview);
  }, [setPage]);

  const goReview = useCallback(() => {
    if (payrollEditIntent) {
      setReviewIntent({
        bucket: 'unsubmitted',
        employeeID: payrollEditIntent.employeeID,
        altEmployeeID: payrollEditIntent.altEmployeeID,
        name: payrollEditIntent.name,
      });
    }
    setPage(Page.TimesheetReview);
  }, [payrollEditIntent, setReviewIntent, setPage]);

  const goSubmittedWeek = useCallback(() => {
    if (payrollEditIntent) {
      setReviewIntent({
        employeeID: payrollEditIntent.employeeID,
        altEmployeeID: payrollEditIntent.altEmployeeID,
        name: payrollEditIntent.name,
      });
    }
    setPage(Page.TimesheetReview);
  }, [payrollEditIntent, setReviewIntent, setPage]);

  // ── Early exits ─────────────────────────────────────────────────────────────
  if (config.payrollTimesheetEditing?.enabled !== true) return null;

  if (employeeID == null) {
    return (
      <div className="ptev2">
        <div className="pte-invite">
          <span className="eic"><Pencil /></span>
          <h3>Pick an employee to edit</h3>
          <p>
            Open an Unsubmitted employee on the Timesheet Review board and choose
            <b> Edit as payroll</b> — the editor works on one employee&rsquo;s week at a time.
          </p>
          <button className="pte-btn" onClick={goBoard} type="button">
            <ChevronLeft /> Back to Timesheet Review
          </button>
        </div>
      </div>
    );
  }

  const editable: PayrollTimesheetStateEditable | null = state && !state.exists ? state : null;
  const fullName = editable
    ? `${editable.name?.first ?? ''} ${editable.name?.last ?? ''}`.trim()
    : `${payrollEditIntent?.name?.first ?? ''} ${payrollEditIntent?.name?.last ?? ''}`.trim();
  const firstName = editable?.name?.first ?? payrollEditIntent?.name?.first ?? 'the employee';
  const altID = editable?.altEmployeeID ?? payrollEditIntent?.altEmployeeID ?? '';
  const entryTotal = editable?.entryCounts?.total ?? 0;

  // ── Row rendering ───────────────────────────────────────────────────────────
  const renderMarks = (entry: EditorEntry) => {
    const marks: React.ReactNode[] = [];
    const prov = (src: string | null | undefined, key: string) => {
      if (src === 'PAYROLL') {
        marks.push(
          <span key={key} className="pe-pv" title="Created by payroll — full audit in history">
            <Shield /><span className="pl">Payroll</span>
          </span>
        );
      } else if (src === 'SYSTEM') {
        marks.push(
          <span key={key} className="pe-pv sys" title="System-generated">
            <Settings /><span className="pl">System</span>
          </span>
        );
      }
    };
    const punchMarks = (row: { punchTypeIn?: number | null; punchTypeOut?: number | null; inLatitude?: number | null; inLongitude?: number | null; createdSource?: string | null }) => {
      if (row.punchTypeIn === 2 || row.punchTypeOut === 2) {
        marks.push(<span key="pt2" className="pe-mk edit" title="Manually added punch"><Plus /></span>);
      }
      if (row.punchTypeIn === 3 || row.punchTypeOut === 3) {
        marks.push(<span key="pt3" className="pe-mk edit" title="Manually edited punch"><Pencil /></span>);
      }
      if (row.inLatitude != null && row.inLongitude != null) {
        marks.push(<span key="gps" className="pe-mk gps" title="GPS captured"><MapPin /></span>);
      } else if (row.createdSource === 'PAYROLL') {
        marks.push(<span key="nogps" className="pe-mk nogps" title="No GPS — created by payroll"><MapPin /></span>);
      }
    };
    switch (entry.kind) {
      case 'hourly':
      case 'pieceHours':
        prov(entry.hourly.row.createdSource, 'pv');
        punchMarks(entry.hourly.row);
        break;
      case 'piece':
        prov(entry.piece.createdSource, 'pv');
        punchMarks(entry.piece);
        break;
      case 'task': prov(entry.task.row.createdSource, 'pv'); break;
      case 'miscTask': prov(entry.misc.row.createdSource, 'pv'); break;
      case 'cswTask': prov(entry.csw.createdSource, 'pv'); break;
      case 'operationalTask': prov(entry.op.createdSource, 'pv'); break;
      case 'sickClaim': {
        prov(entry.sick.createdSource, 'pv');
        if ((entry.sick.minutesClaimed ?? 0) > 0 && !(entry.sick.hourlyAmount && entry.sick.hourlyAmount > 0)) {
          marks.push(
            <span key="rate" className="pe-pv rate" title="Sick rate must be entered before payroll approval">
              <AlertCircle /><span className="pl">Rate</span>
            </span>
          );
        }
        break;
      }
      default: break;
    }
    return marks.length > 0 ? marks : <span className="dash">—</span>;
  };

  const renderRow = (entry: EditorEntry, ymd: string) => {
    const K = ENTRY_KINDS[entry.kind];
    const Icon = K.Icon;
    let d1 = '';
    let d2 = '';
    let time: React.ReactNode = null;
    let dur = '';
    let amt = '';

    const clockCell = (inISO?: string | null, inOff?: string | null, outISO?: string | null, outOff?: string | null) => {
      const i = instantToLocalWallTime(inISO ?? null, inOff ?? null);
      const o = outISO ? instantToLocalWallTime(outISO, outOff ?? null) : null;
      return (
        <>
          {i ? formatClockMinutes(i.minutes) : '—'}
          <span className="ar">→</span>
          {o ? formatClockMinutes(o.minutes) : 'open'}
        </>
      );
    };

    switch (entry.kind) {
      case 'pieceHours': {
        const g = entry.hourly.group;
        const r = entry.hourly.row;
        const meal = r.type === TimeEntryType.Meal ? 'Meal · ' : '';
        d1 = `${meal}${g.project?.altProjectID ?? ''} · ${g.project?.name ?? ''}`;
        d2 = 'Piece hours';
        time = clockCell(r.inDateTime, r.inDateTimeOffset, r.outDateTime, r.outDateTimeOffset);
        dur = r.outDateTime ? formatHours(punchMinutes(r.inDateTime, r.outDateTime)) : '';
        break;
      }
      case 'hourly': {
        const g = entry.hourly.group;
        const r = entry.hourly.row;
        const meal = r.type === TimeEntryType.Meal ? 'Meal · ' : '';
        d1 = g.project?.projectID == null
          ? `${meal}No job — bare hours`
          : `${meal}${g.project.altProjectID} · ${g.project.name}`;
        const acct = g.account?.accountCodeID != null ? `${g.account.altAccountCodeID} ${g.account.name}` : 'No account';
        d2 = g.projectPhase?.projectPhaseID != null ? `${acct} · ${g.projectPhase.name}` : acct;
        // 2020 shapes (clockHourlyV2Config); 'legacy' and 'job' keep the text above.
        if (entry.shape === 'nonJob') {
          d1 = `${meal}Non-Job Time`;
          // The 2020PH non-job account is itself named "Non-Job Time" — don't say it twice.
          const acctName = (g.account?.name ?? '').trim().toLowerCase();
          d2 = acctName && acctName !== 'non-job time' ? acct : 'Hourly · no job costing';
        } else if (entry.shape === 'serviceOrder') {
          d1 = `${meal}${soRowLabel(r, g)}`;
          d2 = acct;
        }
        time = clockCell(r.inDateTime, r.inDateTimeOffset, r.outDateTime, r.outDateTimeOffset);
        dur = r.outDateTime ? formatHours(punchMinutes(r.inDateTime, r.outDateTime)) : '';
        break;
      }
      case 'piece': {
        const r = entry.piece;
        d1 = r.type === TimeEntryType.Meal ? 'Meal break' : 'Piece work';
        d2 = r.createdSource === 'PAYROLL' ? 'Entered by payroll' : 'Clocked on the app';
        time = clockCell(r.inDateTime, r.inDateTimeOffset, r.outDateTime, r.outDateTimeOffset);
        dur = r.outDateTime ? formatHours(punchMinutes(r.inDateTime, r.outDateTime)) : '';
        break;
      }
      case 'task': {
        const r = entry.task.row;
        d1 = `${r.task?.altTaskID ?? ''} · ${r.task?.taskInfo?.description || r.task?.taskInfo?.name || ''}`;
        const proj = entry.task.project ? `${entry.task.project.altProjectID} · ${entry.task.project.name}` : '';
        d2 = [proj, r.intermediateName].filter(Boolean).join(' · ');
        amt = money(r.amountClaimed ?? 0);
        break;
      }
      case 'miscTask': {
        const r = entry.misc.row;
        d1 = r.miscDescription || 'Misc task';
        const acct = r.accountCode ? `${r.accountCode.altAccountCodeID} ${r.accountCode.name}` : '';
        d2 = [acct, r.intermediateName].filter(Boolean).join(' · ');
        amt = money(r.amountClaimed ?? 0);
        break;
      }
      case 'cswTask': {
        const r = entry.csw;
        d1 = r.customerServiceWarranty?.name ?? 'Warranty';
        d2 = r.description ?? '';
        dur = formatHours(r.minutesClaimed ?? 0);
        amt = r.amountClaimed ? money(r.amountClaimed) : '—';
        break;
      }
      case 'sickClaim': {
        const r = entry.sick;
        d1 = r.description || 'Sick';
        d2 = r.hourlyAmount && r.hourlyAmount > 0 ? `Rate ${money(r.hourlyAmount)}/hr` : 'Rate not entered';
        dur = formatHours(r.minutesClaimed ?? 0);
        amt = r.hourlyAmount && r.hourlyAmount > 0
          ? money(Math.round(r.hourlyAmount * ((r.minutesClaimed ?? 0) / 60) * 100) / 100)
          : '—';
        break;
      }
      case 'operationalTask': {
        const r = entry.op;
        d1 = r.operationalTask?.name ?? 'Operational task';
        const acct = r.operationalTask?.accountCode?.accountCodeID != null
          ? `${r.operationalTask.accountCode.altAccountCodeID} ${r.operationalTask.accountCode.name}`
          : '';
        d2 = [acct, r.description].filter(Boolean).join(' · ');
        dur = formatHours(r.minutesClaimed ?? 0);
        // The stored amount is SERVER-computed (rate × hours) — display what the
        // server returned, never a client recomputation.
        amt = r.amountClaimed != null ? money(r.amountClaimed) : '—';
        break;
      }
      default: break;
    }

    return (
      <div className="pe-row" key={`${entry.kind}-${entry.id}`}>
        <span className="pe-kind" style={{ color: K.color, background: K.bg }}>
          <Icon /><span className="kl">{K.label}</span>
        </span>
        <span className="pe-det">
          <span className="d1">{d1}</span>
          <span className="d2">{d2}</span>
        </span>
        <span className="pe-time">{time}</span>
        <span className="pe-dur">{dur}</span>
        <span className="pe-amt">{amt}</span>
        <span className="pe-marks">{renderMarks(entry)}</span>
        <span className="pe-acts">
          <button
            className="pe-iact"
            title="Edit"
            onClick={() => setModal({ kind: entry.kind, ymd, entry, carry: null, nonce: Date.now() })}
            type="button"
          >
            <Pencil />
          </button>
          <button
            className="pe-iact del"
            title="Delete"
            onClick={() => setDeleteTarget({ entry, ymd })}
            type="button"
          >
            <Trash2 />
          </button>
        </span>
      </div>
    );
  };

  // Work order hour claims (TAS-291) — read-only here: they are claimed on the
  // phone and paid at the rate snapshotted on the claim, so payroll sees exactly
  // what the timecard will print. No add/edit/delete from this console.
  const renderWorkOrderRow = (c: EmployeeWorkOrderTask) => {
    const rate = c.hourlyRate ?? c.workOrderTask?.laborPerHour ?? 0;
    const proj = c.project ? `${c.project.altProjectID ?? ''} · ${c.project.name ?? ''}`.replace(/^ · /, '') : '';
    const rateText = `Rate ${money(rate)}/hr${c.rateSource === 'override' ? ' · WO override' : ''}`;
    return (
      <div className="pe-row" key={`workOrder-${c.employeeWorkOrderTaskID}`}>
        <span className="pe-kind" style={{ color: '#7c4dcc', background: '#f0eafa' }}>
          <ClipboardList /><span className="kl">WO</span>
        </span>
        <span className="pe-det">
          <span className="d1">{c.workOrderTask?.name ?? 'Work order'}</span>
          <span className="d2">{[proj, rateText].filter(Boolean).join(' · ')}</span>
        </span>
        <span className="pe-time" />
        <span className="pe-dur">{formatHours(Math.round((c.hoursClaimed ?? 0) * 60))}</span>
        <span className="pe-amt">{money(c.amountImplied ?? 0)}</span>
        <span className="pe-marks" />
        <span className="pe-acts" />
      </div>
    );
  };

  // 2020 drive time (clockHourlyV2Config tenants only) — system-generated TEH
  // rows linked to a DriveTimeEntry; read-only here, like work order claims.
  const renderDriveTimeRow = ({ row: r, group: g }: DriveTimeRow) => {
    const job = g.project?.projectID != null ? `${g.project.altProjectID} · ${g.project.name}` : '';
    const acct = g.account?.accountCodeID != null ? `${g.account.altAccountCodeID} ${g.account.name}` : '';
    const i = instantToLocalWallTime(r.inDateTime ?? null, r.inDateTimeOffset ?? null);
    const o = r.outDateTime ? instantToLocalWallTime(r.outDateTime, r.outDateTimeOffset ?? null) : null;
    return (
      <div className="pe-row" key={`driveTime-${r.timeEntryHourlyID}`}>
        <span className="pe-kind" style={{ color: '#6b7686', background: '#eff1f3' }}>
          <Truck /><span className="kl">Drive</span>
        </span>
        <span className="pe-det">
          <span className="d1">Drive time · system</span>
          <span className="d2">{[job, acct].filter(Boolean).join(' · ')}</span>
        </span>
        <span className="pe-time">
          {i ? formatClockMinutes(i.minutes) : '—'}
          <span className="ar">→</span>
          {o ? formatClockMinutes(o.minutes) : 'open'}
        </span>
        <span className="pe-dur">{r.outDateTime ? formatHours(punchMinutes(r.inDateTime, r.outDateTime)) : ''}</span>
        <span className="pe-amt" />
        <span className="pe-marks" />
        <span className="pe-acts" />
      </div>
    );
  };

  const renderDayCard = (day: Day) => {
    const ymd = String(day.date).slice(0, 10);
    const parts = ymdParts(ymd);
    const { byKind: entries, driveTime } = collectDayEntries(day, hourlyCfg);
    const kindsWithRows = (Object.keys(ENTRY_KINDS) as EditorKind[])
      .filter((k) => entries[k].length > 0)
      .sort((a, b) => ENTRY_KINDS[a].order - ENTRY_KINDS[b].order);
    const woClaims = day.employeeWorkOrderTasks ?? [];
    const woAmount = woClaims.reduce((n, c) => n + (c.amountImplied ?? 0), 0);
    const isEmpty = kindsWithRows.length === 0 && woClaims.length === 0 && driveTime.length === 0;
    const isCollapsed = collapsed[ymd] === true;
    const sickMin = (day.employeeSickClaims ?? []).reduce((n, s) => n + (s.minutesClaimed ?? 0), 0);
    const srvDay = preview?.days?.find((d) => d.date === ymd) ?? null;

    return (
      <section className={`pe-day${isCollapsed ? ' collapsed' : ''}${isEmpty ? ' empty' : ''}`} key={ymd}>
        <header className="pe-dhead">
          <button
            className="pe-dcol"
            onClick={() => setCollapsed((prev) => ({ ...prev, [ymd]: !prev[ymd] }))}
            type="button"
          >
            <ChevronDown />
            <span className="pe-dw">{parts.weekday}</span>
            <span className="pe-dd">{parts.md}</span>
          </button>
          <div className="pe-dtot">
            {(day.totalWorkMinutes ?? 0) > 0 && (
              <span className="pe-mchip"><i>hrs</i>{formatHours(day.totalWorkMinutes)}</span>
            )}
            {srvDay != null && srvDay.overtimeMinutes > 0 && (
              <span className="pe-mchip ot srv" title="From the server preview calculation">
                <i>ot</i>{formatHours(srvDay.overtimeMinutes)}
              </span>
            )}
            {srvDay != null && srvDay.doubleTimeMinutes > 0 && (
              <span className="pe-mchip dt srv" title="From the server preview calculation">
                <i>dt</i>{formatHours(srvDay.doubleTimeMinutes)}
              </span>
            )}
            {sickMin > 0 && <span className="pe-mchip"><i>sick</i>{formatHours(sickMin)}</span>}
            {(day.totalClaimedAmount ?? 0) > 0 && (
              <span className="pe-mchip amt"><i>claimed</i>{money(day.totalClaimedAmount)}</span>
            )}
            {woAmount > 0 && (
              <span className="pe-mchip amt" title="Work order hour claims"><i>work order</i>{money(woAmount)}</span>
            )}
            {isEmpty && <span className="pe-hint">No entries</span>}
            <span className="pe-addwrap">
              <button
                className="pe-addbtn"
                onClick={(ev) => { ev.stopPropagation(); setAddMenuYmd(addMenuYmd === ymd ? null : ymd); }}
                type="button"
              >
                <Plus /> Add
              </button>
              {addMenuYmd === ymd && (
                <span className="pe-addmenu">
                  {enabledKinds.map((k) => {
                    const K = ENTRY_KINDS[k];
                    return (
                      <button
                        key={k}
                        onClick={() => {
                          setAddMenuYmd(null);
                          setModal({ kind: k, ymd, entry: null, carry: null, nonce: Date.now() });
                        }}
                        type="button"
                      >
                        <span className="n" style={{ color: K.color }}>{K.label[0]}</span>
                        {K.section}
                      </button>
                    );
                  })}
                </span>
              )}
            </span>
          </div>
        </header>
        {!isCollapsed && (
          <div className="pe-body">
            {kindsWithRows.map((k) => (
              <div className="pe-sect" key={k}>
                <div className="pe-slabel">
                  <span className="nm">{ENTRY_KINDS[k].section}</span>
                  <span className="grow" />
                  {enabledKinds.includes(k) && (
                    <button
                      className="pe-addbtn"
                      onClick={() => setModal({ kind: k, ymd, entry: null, carry: null, nonce: Date.now() })}
                      type="button"
                    >
                      <Plus /> Add
                    </button>
                  )}
                </div>
                {entries[k].map((entry) => renderRow(entry, ymd))}
              </div>
            ))}
            {driveTime.length > 0 && (
              <div className="pe-sect" key="driveTime">
                <div className="pe-slabel">
                  <span className="nm">Drive time</span>
                  <span className="grow" />
                  <span className="pe-hint">System-generated · not editable here</span>
                </div>
                {driveTime.map(renderDriveTimeRow)}
              </div>
            )}
            {woClaims.length > 0 && (
              <div className="pe-sect" key="workOrder">
                <div className="pe-slabel">
                  <span className="nm">Work order claims</span>
                  <span className="grow" />
                  <span className="pe-hint">Claimed on the app · not editable here</span>
                </div>
                {woClaims.map(renderWorkOrderRow)}
              </div>
            )}
            {isEmpty && (
              <div className="pe-dayempty">
                <Plus /> Nothing on {parts.short} — use <b>Add</b> to start the day.
              </div>
            )}
          </div>
        )}
      </section>
    );
  };

  // ── Modal host ──────────────────────────────────────────────────────────────
  const renderModal = () => {
    if (!modal || !editable) return null;
    const common = {
      employeeID: editable.employeeID,
      payPeriodID,
      ymd: modal.ymd,
      employeeName: fullName,
      editSessionID,
      onCancel: () => setModal(null),
      onConflict: handleConflict,
    };
    switch (modal.kind) {
      case 'pieceHours':
        return (
          <PieceHoursModal
            key={modal.nonce}
            {...common}
            entry={modal.entry?.kind === 'pieceHours' ? modal.entry.hourly : null}
            carry={modal.carry as PieceHoursCarry | null}
            onSaved={handleModalSaved}
          />
        );
      case 'hourly':
        return (
          <HourlyEntryModal
            key={modal.nonce}
            {...common}
            entry={modal.entry?.kind === 'hourly' ? modal.entry.hourly : null}
            initialShape={modal.entry?.kind === 'hourly' ? modal.entry.shape : undefined}
            carry={modal.carry as HourlyCarry | null}
            onSaved={handleModalSaved}
          />
        );
      case 'piece':
        return (
          <PieceEntryModal
            key={modal.nonce}
            {...common}
            entry={modal.entry?.kind === 'piece' ? modal.entry.piece : null}
            carry={modal.carry as PieceCarry | null}
            onSaved={handleModalSaved}
          />
        );
      case 'task':
        return (
          <TaskClaimModal
            key={modal.nonce}
            {...common}
            entry={modal.entry?.kind === 'task' ? modal.entry.task : null}
            carry={modal.carry as TaskCarry | null}
            onSaved={handleModalSaved}
          />
        );
      case 'miscTask':
        return (
          <MiscTaskModal
            key={modal.nonce}
            {...common}
            entry={modal.entry?.kind === 'miscTask' ? modal.entry.misc : null}
            carry={modal.carry as MiscCarry | null}
            onSaved={handleModalSaved}
          />
        );
      case 'cswTask':
        return (
          <CswTaskModal
            key={modal.nonce}
            {...common}
            entry={modal.entry?.kind === 'cswTask' ? modal.entry.csw : null}
            carry={modal.carry as CswCarry | null}
            onSaved={handleModalSaved}
          />
        );
      case 'sickClaim':
        return (
          <SickClaimModal
            key={modal.nonce}
            {...common}
            entry={modal.entry?.kind === 'sickClaim' ? modal.entry.sick : null}
            carry={modal.carry as SickCarry | null}
            onSaved={handleModalSaved}
          />
        );
      case 'operationalTask':
        return (
          <OperationalTaskModal
            key={modal.nonce}
            {...common}
            hourlyRate={editable.hourlyRate ?? 0}
            entry={modal.entry?.kind === 'operationalTask' ? modal.entry.op : null}
            carry={modal.carry as OperationalCarry | null}
            onSaved={handleModalSaved}
          />
        );
      default:
        return null;
    }
  };

  // ── Views ───────────────────────────────────────────────────────────────────
  const crumbs = (
    <div className="pe-crumbs">
      <button onClick={goBoard} type="button">Timesheet Review</button>
      <ChevronRight />
      <button onClick={goReview} type="button">{fullName || `Employee #${employeeID}`}</button>
      <ChevronRight />
      <b>Edit as payroll</b>
    </div>
  );

  if (loading) {
    return (
      <div className="ptev2">
        {crumbs}
        <div className="pte-invite">
          <span className="eic"><Pencil /></span>
          <h3>Loading the week…</h3>
          <p>Reading {fullName || 'the employee'}&rsquo;s editable state for {periodLabel}.</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="ptev2">
        {crumbs}
        <div className="pte-invite">
          <span className="eic warn"><AlertCircle /></span>
          <h3>Could not open the editor</h3>
          <p>{loadError}</p>
          <button className="pte-btn" onClick={goBoard} type="button">
            <ChevronLeft /> Back to Timesheet Review
          </button>
        </div>
      </div>
    );
  }

  // Already submitted — directed handoff, never an error toast (iteration-1 shot 16).
  if (state?.exists) {
    return (
      <div className="ptev2">
        {crumbs}
        <div className="pte-invite">
          <span className="eic"><CheckCircle /></span>
          <h3>{firstName} submitted this week already</h3>
          <p>
            A timesheet now exists for {periodLabel} (<span className="mono">#{state.timesheetID}</span>),
            so it is no longer editable here. Review it on the board, or unlock it first if it still
            needs corrections.
          </p>
          <div className="pte-invite-acts">
            <button className="pte-btn primary" onClick={goSubmittedWeek} type="button">
              <ArrowRight /> Review it on the board
            </button>
            <button className="pte-btn" onClick={() => setHistoryOpen(true)} type="button">
              <History /> View change history
            </button>
          </div>
        </div>
        <HistoryPanel
          open={historyOpen}
          employeeID={employeeID}
          payPeriodID={payPeriodID}
          employeeName={fullName || `Employee #${employeeID}`}
          periodLabel={periodLabel}
          onClose={() => setHistoryOpen(false)}
        />
      </div>
    );
  }

  if (!editable) return null;

  const week = preview?.week ?? null;

  return (
    <div className="ptev2">
      {crumbs}

      {/* identity header */}
      <div className="pe-idcard">
        <span className={`pe-idav${editable.active === false ? ' off' : ''}`}>
          {initials(editable.name?.first, editable.name?.last)}
        </span>
        <div className="pe-idmain">
          <div className="pe-idname">
            <h2>{fullName}</h2>
            <span className="id">#{altID}</span>
            {editable.active === false && <InactiveBadge />}
          </div>
          <div className="pe-idstatus">
            <span className="pe-pill">Unsubmitted</span>
            <span className="editing">editing as payroll</span>
            <span className="period"><Calendar /> {periodLabel}</span>
          </div>
          <div className="pe-idmeta">
            {editable.jobTitle && <span>{editable.jobTitle}</span>}
            {editable.phoneNumber && <span>{editable.phoneNumber}</span>}
            {editable.assignedSupervisor?.employeeID !== 0 && (
              <span>
                Supervisor {`${editable.assignedSupervisor.name?.first ?? ''} ${editable.assignedSupervisor.name?.last ?? ''}`.trim()}
              </span>
            )}
            <span>{entryTotal === 0 ? 'No entries' : `${entryTotal} entries`}</span>
          </div>
        </div>
        <div className="pe-idacts">
          <button className="pte-btn" onClick={() => setHistoryOpen(true)} type="button">
            <History /> Change history
          </button>
          <button className="pte-btn" onClick={goReview} type="button">
            <ArrowRight /> Back to review
          </button>
          <button
            className="pte-btn primary"
            onClick={() => setSubmitOpen(true)}
            disabled={entryTotal === 0}
            title={entryTotal === 0 ? 'Add at least one entry before submitting' : undefined}
            type="button"
          >
            <CheckCircle /> Submit for employee
          </button>
        </div>
      </div>

      {/* session bar — M2 informational; M3 drops preview/commit controls here */}
      <div className="pe-session">
        <History />
        <span className="grow">
          Edit session started <b>{sessionStart}</b> · <b>{sessionChanges}</b> change{sessionChanges === 1 ? '' : 's'} recorded
          this session{sessionChanges > 0 ? ' · saved as you go' : ''}
        </span>
        {sessionChanges > 0 && (
          <button className="pe-linklike" onClick={() => setHistoryOpen(true)} type="button">
            View changes
          </button>
        )}
        {/* M3 drops preview/commit controls here — no placeholder chip until then. */}
      </div>

      {/* zero-entry invitation */}
      {entryTotal === 0 && (
        <div className="pte-invite inline">
          <span className="eic"><Calendar /></span>
          <h3>Nothing recorded for this week</h3>
          <p>
            Build {firstName}&rsquo;s week from scratch — add the first entry to any day below.
            Submit turns on once there is at least one entry.
          </p>
          <div className="kinds">
            {enabledKinds.map((k) => {
              const K = ENTRY_KINDS[k];
              const Icon = K.Icon;
              return (
                <span key={k} className="pe-kind" style={{ color: K.color, background: K.bg }}>
                  <Icon /><span className="kl">{K.label}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* day cards */}
      <div className="pe-days">
        {(editable.days ?? []).map(renderDayCard)}
      </div>

      {/* sticky totals footer — server preview numbers only */}
      <div className={`pe-foot${previewBusy ? ' busy' : ''}`}>
        <div className="pe-footin">
          <span className="pe-ftot"><span className="l">Reg</span><span className="v">{week ? formatHours(week.regularMinutes) : '—'}</span></span>
          <span className="pe-ftot"><span className="l">OT</span><span className="v ot">{week ? formatHours(week.overtimeMinutes) : '—'}</span></span>
          <span className="pe-ftot"><span className="l">DT</span><span className="v dt">{week ? formatHours(week.doubleTimeMinutes) : '—'}</span></span>
          <span className="pe-fsep" />
          <span className="pe-ftot"><span className="l">Sick</span><span className="v">{week ? formatHours(week.sickMinutes) : '—'}</span></span>
          <span className="pe-fsep" />
          <span className="pe-ftot"><span className="l">Claimed</span><span className="v">{week ? money(week.claimedAmount) : '—'}</span></span>
          <span className="pe-busy" aria-hidden="true"><span className="sp" />calculating</span>
          <span className="grow" />
          {flash && <span className="pe-flash on">{flash}</span>}
          <span className="pe-hint"><Info /> Totals come from the server&rsquo;s preview calculation after each save</span>
        </div>
      </div>

      {/* overlays */}
      {renderModal()}

      {deleteTarget && (
        <ConfirmModal
          variant="warning"
          icon={<Trash2 size={22} />}
          title={`Delete this ${ENTRY_KINDS[deleteTarget.entry.kind].noun}?`}
          subtitle="The deletion is recorded in the change history."
          detail={<div><strong>{describeEntry(deleteTarget.entry)}</strong><br />{ymdParts(deleteTarget.ymd).weekday} · {fullName}</div>}
          confirmLabel="Delete"
          loading={deleting}
          onConfirm={confirmDelete}
          onClose={() => { if (!deleting) setDeleteTarget(null); }}
        />
      )}

      {submitOpen && (
        <PayrollSubmitWizard
          state={editable}
          payPeriodID={payPeriodID}
          payPeriodLabel={periodLabel}
          onClose={() => setSubmitOpen(false)}
          onSuccess={() => {
            setSubmitOpen(false);
            createPopup(
              `Submitted for ${fullName}`,
              `Recorded as Submitted by Payroll${userName ? ` · ${userName}` : ''}. The timesheet moved to the review flow.`
            );
            goBoard();
          }}
          onOpenSubmittedWeek={() => { setSubmitOpen(false); goSubmittedWeek(); }}
          onBackToBoard={() => { setSubmitOpen(false); goBoard(); }}
        />
      )}

      <HistoryPanel
        open={historyOpen}
        employeeID={employeeID}
        payPeriodID={payPeriodID}
        employeeName={fullName || `Employee #${employeeID}`}
        periodLabel={periodLabel}
        onClose={() => setHistoryOpen(false)}
      />
    </div>
  );
};

export default PayrollTimesheetEditPage;
